"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useLayoutEffect, useMemo, useReducer, useRef } from "react";
import { cn, renderRichText } from "@/lib/utils";
import { COLOR_SCHEMES } from "@/lib/colorSchemes";
import { Button } from "@/components/ui/button";
import type { BannerProps } from "./types";
import {
  BANNER_LAYOUT,
  BANNER_CONTENT_ALIGN_CLASSES,
  HEADING_SIZE_CLASS,
  Z,
  safeColor,
} from "./types";

// -----------------------------------------------------------------------------
// HeroBanner — the "hero" layout with side-by-side text + foreground image,
// optional rotating word animation, and accent-word highlighting.
//
// Handles variants "hero" and "rotating-word".
// -----------------------------------------------------------------------------

// ── Rotating-word state machine ─────────────────────────────

type WordPhase = "visible" | "exit" | "enter";

interface WordState {
  index: number;
  phase: WordPhase;
}

type WordAction =
  | { type: "tick-exit" }
  | { type: "tick-swap"; total: number }
  | { type: "tick-settle" };

function wordReducer(state: WordState, action: WordAction): WordState {
  switch (action.type) {
    case "tick-exit":
      return { ...state, phase: "exit" };
    case "tick-swap":
      return { index: (state.index + 1) % action.total, phase: "enter" };
    case "tick-settle":
      return { ...state, phase: "visible" };
  }
}

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

// ── Component ───────────────────────────────────────────────

export function HeroBanner({
  variant = "hero",
  eyebrow,
  title = "",
  rotatingWords = [],
  description,
  cta,
  colorScheme = "default",
  image,
  accentWordColor,
  layout = "7/5",
  reversed = false,
  headingSize = "banner",
  contentAlign = "center-left",
}: BannerProps) {
  const scheme = COLOR_SCHEMES[colorScheme];
  const lc = BANNER_LAYOUT[layout] ?? BANNER_LAYOUT["7/5"];
  const alignCls =
    BANNER_CONTENT_ALIGN_CLASSES[contentAlign] ??
    BANNER_CONTENT_ALIGN_CLASSES["center-left"];

  // ── Title parsing ───────────────────────────────────────
  const hasPlaceholder = title.includes("{word}");
  const isRotating = variant === "rotating-word";
  const showRotation = isRotating && hasPlaceholder && rotatingWords.length > 1;
  const staticWord = hasPlaceholder ? (rotatingWords[0] ?? "") : "";
  const showAccentWord =
    hasPlaceholder && (isRotating || staticWord.length > 0);
  const firstPlaceholderIdx = title.indexOf("{word}");
  const before = hasPlaceholder ? title.slice(0, firstPlaceholderIdx) : title;
  const after = hasPlaceholder
    ? title.slice(firstPlaceholderIdx + "{word}".length)
    : "";
  const hasRotation = showRotation;

  // ── Rotating-word state ─────────────────────────────────
  const [wordState, dispatch] = useReducer(wordReducer, {
    index: 0,
    phase: "visible" as WordPhase,
  });
  const activeWord = isRotating
    ? (rotatingWords[wordState.index] ?? "")
    : staticWord;

  // ── Pre-measure word widths ─────────────────────────────
  const measureRef = useRef<HTMLSpanElement | null>(null);
  const widthsRef = useRef<number[]>([]);

  const remeasure = () => {
    if (!showAccentWord || !measureRef.current) return;
    const node = measureRef.current;
    const widths: number[] = [];
    for (const word of rotatingWords) {
      node.textContent = word;
      widths.push(node.offsetWidth);
    }
    node.textContent = "";
    widthsRef.current = widths;
  };

  useIsomorphicLayoutEffect(() => {
    remeasure();
  }, [showAccentWord, rotatingWords]);

  useEffect(() => {
    if (!showAccentWord) return;
    const onResize = () => remeasure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [showAccentWord, rotatingWords]);

  const currentWidth = widthsRef.current[wordState.index];

  // ── Animation ticker ────────────────────────────────────
  useEffect(() => {
    if (!hasRotation) return;
    const VISIBLE_MS = 2500;
    const EXIT_MS = 300;
    const SETTLE_DELAY_MS = 20;

    let exitTimer: ReturnType<typeof setTimeout>;
    let swapTimer: ReturnType<typeof setTimeout>;
    let settleTimer: ReturnType<typeof setTimeout>;

    const runCycle = () => {
      exitTimer = setTimeout(() => {
        dispatch({ type: "tick-exit" });
        swapTimer = setTimeout(() => {
          dispatch({ type: "tick-swap", total: rotatingWords.length });
          settleTimer = setTimeout(() => {
            dispatch({ type: "tick-settle" });
            runCycle();
          }, SETTLE_DELAY_MS);
        }, EXIT_MS);
      }, VISIBLE_MS);
    };

    runCycle();

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(swapTimer);
      clearTimeout(settleTimer);
    };
  }, [hasRotation, rotatingWords.length]);

  const resolvedAccent = useMemo(
    () => safeColor(accentWordColor, "#19557f"),
    [accentWordColor],
  );

  return (
    <>
      {/* Off-screen measurement node */}
      {showAccentWord && (
        <span
          ref={measureRef}
          aria-hidden
          className={`${HEADING_SIZE_CLASS[headingSize]} font-semibold absolute -left-[9999px] top-0 whitespace-pre pointer-events-none`}
        />
      )}

      {/* Flex layout — text + image */}
      <div className="relative flex-1 flex flex-col lg:flex-row lg:gap-8 max-w-content mx-auto w-full">
        {/* Text column */}
        <div
          className={cn(
            "shrink-0 grow-0 min-w-0",
            reversed ? lc.image : lc.text,
            reversed && "lg:order-last",
            "relative",
          )}
          style={{ zIndex: Z.text }}
        >
          <div
            className={cn(
              alignCls,
              "mt-18 lg:mt-24 max-w-[700px] mx-auto lg:mx-0",
              !image && "max-lg:pb-18",
            )}
          >
            {eyebrow && (
              <p className={cn("body-md font-semibold", scheme.bodyMuted)}>
                {eyebrow}
              </p>
            )}

            <h1
              className={cn(
                HEADING_SIZE_CLASS[headingSize],
                "font-semibold",
                scheme.heading,
              )}
            >
              {before}
              {showAccentWord &&
                activeWord &&
                (showRotation ? (
                  <span
                    className="relative inline-block overflow-hidden align-bottom leading-[1.2] pb-[0.15em] -mb-[0.15em]"
                    style={{ width: currentWidth }}
                  >
                    <span
                      className="inline-block whitespace-pre will-change-transform"
                      style={{
                        color: resolvedAccent,
                        transform:
                          wordState.phase === "exit"
                            ? "translateY(-110%)"
                            : wordState.phase === "enter"
                              ? "translateY(110%)"
                              : "translateY(0)",
                        transition:
                          wordState.phase === "enter"
                            ? "none"
                            : "transform 300ms ease-in-out",
                      }}
                    >
                      {activeWord}
                    </span>
                  </span>
                ) : (
                  <span style={{ color: resolvedAccent }}>{activeWord}</span>
                ))}
              {after}
            </h1>

            <div className="flex flex-col gap-4 pt-4 lg:flex-row lg:items-start lg:gap-6">
              {description && (
                <p
                  className={cn(
                    "body-md lg:order-last lg:pt-1",
                    scheme.bodyMuted,
                  )}
                >
                  {renderRichText(description)}
                </p>
              )}
              {cta && (
                <div className="shrink-0">
                  <Button
                    variant="default"
                    size="default"
                    nativeButton={false}
                    render={
                      cta.href.startsWith("#") ? (
                        <a href={cta.href} />
                      ) : (
                        <Link href={cta.href} />
                      )
                    }
                  >
                    {cta.label}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Image column */}
        {image && (
          <div
            className={cn(
              "mt-8 lg:mt-0 relative w-full h-70 lg:h-auto shrink-0 grow-0 min-w-0 overflow-visible",
              reversed ? lc.text : lc.image,
              reversed && "lg:order-first",
            )}
            style={{ zIndex: Z.image }}
          >
            <div className="absolute inset-0 -bottom-13.75 lg:-bottom-18.75">
              <Image
                src={image.src}
                alt={image.alt}
                fill
                priority
                className="object-contain object-bottom"
                sizes="(min-width: 1024px) 42vw, 100vw"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
